import { Box, Grid, Typography } from '@mui/material';
import { CarcinogenicReportWorker } from '../../types';
import { DrawerHeader } from '../../../../../../components/Drawer/DrawerHeader';
import { WorkerInfoCard } from '../WorkerInfoCard';
import CustomField from '../../../../../../components/CustomFormsComponents/CustomField';
import useMultipleWorkerSubstancesForms from '../../hooks/useMultipleWorkerSubstancesForm';
import ButtonActions from '../../../../../../components/ButtonActions';
import { EXPOSITION_OPTIONS } from '../../consts';
import AutocompleteComponent from '../../../../../../components/layout/Autocomplete';
import BackdropComponent from '../../../../../../components/Backdrop/BackdropComponent';
import { TEST_IDS } from '../../testIds';

interface WorkerSubstancesAndMedicalExamsDrawerProps {
    selectedWorker: CarcinogenicReportWorker | null;
    translate: (key: string) => string;
    handleCloseDrawer: () => void;
    onlyVisual: boolean;
}

const WorkerSubstancesAndMedicalExamsDrawer = ({
    handleCloseDrawer,
    selectedWorker,
    translate,
    onlyVisual
}: WorkerSubstancesAndMedicalExamsDrawerProps) => {
    const {
        formsData,
        handleSubmit,
        formConfigs,
        handleChange,
        errors,
        expositionType,
        handleExpositionTypeChange,
        isFetchingWorkerDetail,
        isExposed
    } = useMultipleWorkerSubstancesForms(selectedWorker, handleCloseDrawer);
    
    return (
        <Box
            display="flex"
            justifyContent="space-between"
            sx={{ flexDirection: 'column', height: '100%' }}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <DrawerHeader
                        onIconClick={handleCloseDrawer}
                        text={translate('workerForm.title')}
                    />
                    <WorkerInfoCard
                        name={selectedWorker?.nombre || ''}
                        surname={selectedWorker?.apellido || ''}
                        cuil={selectedWorker?.cuil || '-'}
                        workPosition={selectedWorker?.puestoTrabajo?.nombre || '-'}
                        sector={selectedWorker?.sector?.nombre || '-'}
                        handleDeleteClick={() => {}}
                        translate={translate}
                    />
                </Grid>
                <Grid container justifyContent={'center'} mt={3}>
                    {!isExposed && (
                        <Typography variant="subtitle1">
                            {translate('workerForm.noExposed')}
                        </Typography>
                    )}
                </Grid>
                {isExposed && (
                    <Grid item xs={12}>
                        <AutocompleteComponent
                            options={EXPOSITION_OPTIONS}
                            onlyVisual={onlyVisual}
                            value={expositionType}
                            onChange={(e: any) => handleExpositionTypeChange(e)}
                            templateLabel={(item: any) => item?.descripcion}
                            label={'Tipo de Exposición'}
                            testID={TEST_IDS.editSubstancesAndMedicalExams.expositionType}

                        />
                    </Grid>
                )}

                {isExposed &&
                    formsData.map((formData: any, formIndex) => (
                        <Grid item xs={12} key={formIndex}>
                            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    {`${formData.substanceCode} - ${formData.substanceDescription}`}
                                </Typography>
                                <Grid container spacing={2}>
                                    {formConfigs[formIndex].map(
                                        (field: any, fieldIndex: number) => (
                                            <Grid
                                                item
                                                xs={field?.gridSpace?.xs}
                                                sm={field?.gridSpace?.sm}
                                                md={field?.gridSpace?.md}
                                                lg={field?.gridSpace?.lg}
                                                key={fieldIndex}
                                            >
                                                <CustomField
                                                    config={field}
                                                    value={formData[field.name]}
                                                    onlyVisual={onlyVisual}
                                                    onChange={(name, value) =>
                                                        handleChange(formIndex, name, value)
                                                    }
                                                    error={
                                                        errors[`${formIndex}-${field.name}`]
                                                            ?.hasError || false
                                                    }
                                                    helperText={
                                                        errors[`${formIndex}-${field.name}`]
                                                            ?.errorMessage || field.helperText
                                                    }
                                                />
                                            </Grid>
                                        )
                                    )}
                                </Grid>
                            </Box>
                        </Grid>
                    ))}
            </Grid>
            <Grid
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{ marginTop: '1rem' }}
            >
                <Box
                    sx={{
                        '& > :not(style)': { m: 1 },
                        display: 'flex'
                    }}
                >
                  {!onlyVisual &&  <ButtonActions
                        handleClose={handleCloseDrawer}
                        renderBackAction
                        returnText={translate('workerForm.buttons.cancel')}
                        confirmText={translate('workerForm.buttons.save')}
                        loading={false}
                        disabled={false}
                        onClick={handleSubmit}
                        borderRadius="4px"
                        size="small"
                    />}
                </Box>
            </Grid>
            <BackdropComponent loading={isFetchingWorkerDetail} />
        </Box>
    );
};

export { WorkerSubstancesAndMedicalExamsDrawer };
